import React, { useState } from 'react'
import { Input, InputGroup } from 'rsuite'
import { InfoTooltip, TooltipPlacement } from '../../atoms/info-tooltip/info-tooltip'
import { ReactComponent as WarningIcon } from '../../../assets/svgs/warning.svg'
import './input-field.scss'

interface InputFieldProps {
  fieldType: 'email' | 'password' | 'text'
  label: string
  value: string
  onChange?: (value: string) => void
  placeholder?: string
  error?: string | null
  required?: boolean
  tooltipContent?: string | React.ReactNode
  tooltipPlacement?: TooltipPlacement
  icon?: React.ReactElement
  autoComplete?: boolean
}

export const InputField: React.FC<InputFieldProps> = ({
  label,
  value,
  onChange,
  placeholder = '',
  error,
  fieldType,
  required,
  tooltipContent,
  tooltipPlacement,
  icon,
  autoComplete = true,
}) => {
  const [touched, setTouched] = useState(false)

  const onBlur = () => {
    setTouched(true)
  }

  const showError = touched && error

  return (
    <div className={`input-field ${error ? 'has-error' : ''}`}>
      <div className="label-wrapper">
        <label htmlFor={`${fieldType}-${label}`} className="form-label">
          {label}
          {required && <span className="required-asterisk">*</span>}
        </label>
        {fieldType === 'email' ? (
          <InfoTooltip
            styleClass="input-info"
            tooltipContent={tooltipContent as string | React.ReactNode}
            placement={tooltipPlacement}
          />
        ) : null}
      </div>

      <InputGroup inside className="input-wrapper">
        <Input
          id={`${fieldType}-${label}`}
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          placeholder={placeholder}
          className="label-input "
          required={required}
          aria-describedby={showError ? 'error-message' : undefined}
          aria-invalid={showError ? 'true' : 'false'}
          autoComplete={autoComplete ? 'on' : 'off'}
        />
        {icon && <InputGroup.Button className="input-icon">{icon}</InputGroup.Button>}
      </InputGroup>

      {showError && (
        <>
          <span className="sr-only" role="alert" aria-live="assertive" id="error-message">
            {error}
          </span>
          <div className="error-message">
            <WarningIcon className="warning-symbol" />
            {error}
          </div>
        </>
      )}
    </div>
  )
}

export default InputField
